// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { encodeCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import {
    getPageMetadata,
    getSessionCookies,
    getUrl,
    setSessionCookies,
} from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getSelectedBrandPath,
    getSelectedCategoryPath,
    getSelectedProductPath,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getDealerState } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    image = "image",
}

interface TwitterCardProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.image]: string;
    };
}

const cleanUrl = (authority?: string, url?: string, alternativeUrl = "") => {
    if (!url || !authority) {
        return alternativeUrl;
    }

    if (url.toLowerCase().startsWith("http")) {
        return url;
    }

    return `${authority}${url.startsWith("/") ? url : `/${url}`}`;
};

const mapStateToProps = (state: ApplicationState, ownProps: TwitterCardProps) => {
    const pageType = getCurrentPage(state).type;
    const { pathname, search } = getLocation(state);
    const { title: defaultTitle, description: defaultDescription, image: defaultImage } = ownProps.fields;

    let pageMetadata;

    if (pageType === "BrandDetailsPage") {
        const brandPath = getSelectedBrandPath(state) || pathname;
        const brand = getBrandStateByPath(state, brandPath).value;
        pageMetadata = {
            title: brand?.pageTitle || brand?.name,
            description: brand?.metaDescription,
            image: brand?.featuredImagePath || brand?.logoLargeImagePath,
        };
    } else if (pageType === "ProductDetailsPage") {
        const productPath =
            getSelectedProductPath(state) || (pathname.toLowerCase().startsWith("/content/") ? "" : pathname);
        const product = getProductStateByPath(state, productPath).value;

        pageMetadata = {
            title: product?.content?.openGraphTitle || product?.content?.pageTitle || product?.productTitle,
            description: product?.content?.metaDescription,
            image: product?.content?.openGraphImage || product?.mediumImagePath,
        };
    } else if (pageType === "ProductListPage" || pageType === "CategoryDetailsPage") {
        const selectedCategoryPath = getSelectedCategoryPath(state);
        const path = selectedCategoryPath || (pathname.toLowerCase().startsWith("/content/") ? "" : pathname);
        const productListCatalogPage = getCatalogPageStateByPath(state, path).value;
        if (productListCatalogPage) {
            const { metaDescription, openGraphImage, openGraphTitle, title, primaryImagePath } = productListCatalogPage;
            pageMetadata = {
                title: openGraphTitle || title,
                description: metaDescription,
                image: openGraphImage || primaryImagePath,
            };
        }
    } else if (pageType === "DealerDetailsPage") {
        const parsedQuery = parseQueryString<{ id?: string; invite?: string }>(search);
        const id = parsedQuery.id;
        const dealerDetails = getDealerState(state, id).value;
        if (dealerDetails) {
            pageMetadata = pageMetadata = {
                title: dealerDetails.name,
            };
        }
    } else {
        const metadata = getPageMetadata();
        pageMetadata = pageMetadata = {
            title: metadata?.openGraphTitle || metadata?.title,
            description: metadata?.metaDescription,
            image: metadata?.openGraphImage,
        };
    }

    let url: { protocol: string; host: string } | undefined;
    if (IS_SERVER_SIDE && getUrl()) {
        url = getUrl()!;
    } else if (typeof window !== "undefined") {
        url = window.location;
    }

    const authority = url ? `${url.protocol}//${url.host}` : undefined;

    if (pageMetadata) {
        pageMetadata.title = pageMetadata.title || defaultTitle;
        pageMetadata.description = pageMetadata.description || defaultDescription;
        pageMetadata.image = cleanUrl(authority, pageMetadata.image || defaultImage);
    }

    return {
        pageMetadata,
        websiteName: state.context.website.name,
    };
};

type Props = TwitterCardProps & ReturnType<typeof mapStateToProps> & HasShellContext;

interface State {
    metadataSetForId?: string;
}

interface TwitterCardStyles {
    wrapper: InjectableCss;
    titleText: TypographyPresentationProps;
}

const twitterCardStyles: TwitterCardStyles = {
    wrapper: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
    titleText: {
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const styles = twitterCardStyles;

class TwitterCard extends React.Component<Props, State> {
    UNSAFE_componentWillMount() {
        this.setMetadata();
    }

    componentDidUpdate(): void {
        this.setMetadata();
    }

    setMetadata() {
        const { pageMetadata, websiteName } = this.props;

        if (!pageMetadata || !IS_SERVER_SIDE) {
            return;
        }

        const setTwitterValue = (value?: string) => encodeCookie(value || "");

        setSessionCookies({
            ...getSessionCookies(),
            twitterSite: setTwitterValue(websiteName),
            twitterTitle: setTwitterValue(pageMetadata?.title),
            twitterImage: setTwitterValue(pageMetadata?.image),
            twitterDescription: setTwitterValue(pageMetadata?.description),
        });
    }

    render() {
        const { shellContext } = this.props;

        return (
            <>
                {shellContext.isInShell && shellContext.isEditing && (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography {...styles.titleText}>{translate("Twitter widget")}</Typography>
                    </StyledWrapper>
                )}
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(TwitterCard)),
    definition: {
        group: "Basic",
        icon: "PageTitle",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Default Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.description,
                displayName: "Default Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.image,
                displayName: "Default Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
